import type React from 'react';
import {
  MessageCircle,
  ShoppingCart,
  Package,
  HeadphonesIcon,
} from 'lucide-react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import type { ChatDocument } from 'models/chat';
import type { OfferDocument } from 'models/offer';
import type { MainFilters } from '@util/firestore/messages';
import { UserDocument } from 'models/user';

// Constants
export const SEARCH_DEBOUNCE_MS = 300;

// Offer state mapping for filtering
export const OFFER_STATE_MAP = {
  offers_pending: 0,
  offers_accepted: 1,
  offers_declined: 2,
  offers_expired: 3,
} as const;

// Empty state configurations
export const getEmptyStateContent = (
  filterValue: MainFilters
): {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
} => {
  switch (filterValue) {
    case 'all':
      return {
        icon: MessageCircle,
        title: 'No conversations yet',
        description: 'Start buying or selling to see your conversations here.',
      };
    case 'unread':
      return {
        icon: MessageCircle,
        title: 'No unread messages',
        description: 'All caught up! Check back later for new messages.',
      };
    case 'offers':
      return {
        icon: MagnifyingGlassIcon,
        title: 'No offers found',
        description: 'Make an offer on a product to start negotiating.',
      };
    case 'buy':
      return {
        icon: ShoppingCart,
        title: 'No buying conversations',
        description: 'Start shopping to see conversations with sellers here.',
      };
    case 'sell':
      return {
        icon: Package,
        title: 'No selling conversations',
        description: 'List products for sale to connect with potential buyers.',
      };
    case 'support':
      return {
        icon: HeadphonesIcon,
        title: 'No support tickets',
        description: 'Contact support if you need help with your account.',
      };
    default:
      return {
        icon: MessageCircle,
        title: 'No conversations found',
        description: 'Try adjusting your filter or search criteria.',
      };
  }
};

// Utility to calculate unread counts by filter category
export const calculateUnreadCounts = (
  chats: ChatDocument[],
  userDoc?: UserDocument | null | undefined
): Record<string, number> => {
  const counts: Record<string, number> = {
    all: 0,
    flagged: 0,
    unread: 0,
    offers: 0,
    offers_pending: 0,
    offers_accepted: 0,
    offers_declined: 0,
    offers_expired: 0,
    buying: 0,
    selling: 0,
    support: 0,
  };

  chats.forEach((chat) => {
    // Count all chats that have unread messages for the current user
    const hasUnread = userDoc?.uid
      ? !!chat.unread?.[userDoc.uid] ||
        (chat.is_expert &&
          userDoc.roles?.support &&
          chat.unread?.[chat.seller_id])
      : false;

    if (hasUnread) {
      counts.all += 1;
      counts.unread += 1;

      // Count unread messages in other categories
      if (chat.flagged) {
        counts.flagged += 1;
      }
      if (chat.is_offer) {
        counts.offers += 1;
      }
      if (chat.buyer_id === userDoc?.uid) {
        counts.buying += 1;
      }
      if (chat.seller_id === userDoc?.uid) {
        counts.selling += 1;
      }
      if (chat.case_num !== undefined) {
        counts.support += 1;
      }
    }
  });

  return counts;
};

// Utility to filter chats by offer state
export const filterChatsByOfferState = (
  chats: ChatDocument[],
  offerState: string,
  offers: OfferDocument[] | undefined
): ChatDocument[] => {
  // Return empty array if offers are not available
  if (!offers || offers.length === 0) {
    return [];
  }

  return chats.filter((chat) => {
    const offer = offers.find(
      (offer: OfferDocument) => offer.id === chat.offer_id
    );

    if (!offer) return false;

    const state =
      OFFER_STATE_MAP[`offers_${offerState}` as keyof typeof OFFER_STATE_MAP] ??
      null;

    return offer.state === state;
  });
};

// Utility to extract offer IDs from chat data
export const extractOfferIds = (chats: ChatDocument[]): string[] => {
  const offerChats = chats.filter((chat) => chat.is_offer);

  return offerChats
    .map((chat) => chat.offer_id)
    .filter((id): id is string => Boolean(id));
};
